import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";

import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";

import PageTitle from "../../components/PageTitle";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import LetsTalk from "../../components/letsTalk";

const brandedPlaceholder = (label: string): string => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'><defs><linearGradient id='g' x1='0' x2='1' y1='0' y2='1'><stop offset='0' stop-color='#2f81ad'/><stop offset='1' stop-color='#1d648c'/></linearGradient></defs><rect width='600' height='400' fill='url(%23g)'/><text x='50%' y='50%' fill='white' font-size='26' font-family='-apple-system, system-ui, sans-serif' text-anchor='middle' dominant-baseline='middle' font-weight='700'>${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const HeroBg = styled(Box)(({ theme }) => ({
  backgroundColor: "#0d1620",
  color: "#fff",
  paddingTop: theme.spacing(14),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(8),
  },
}));

const HeroLabel = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 22,
});

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3.2rem",
  fontWeight: 700,
  lineHeight: 1.12,
  letterSpacing: "-0.02em",
  color: "#fff",
  marginBottom: theme.spacing(3),
  maxWidth: 820,
  [theme.breakpoints.down("md")]: { fontSize: "2.4rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.9rem" },
}));

const HeroLead = styled(Typography)(({ theme }) => ({
  fontSize: "1.08rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.72)",
  marginBottom: theme.spacing(4),
  maxWidth: 660,
}));

const PrimaryButton = styled(Button)({
  padding: "12px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const GhostButton = styled(Button)(({ theme }) => ({
  padding: "11px 22px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  backgroundColor: "transparent",
  marginLeft: theme.spacing(1.5),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    borderColor: "rgba(255, 255, 255, 0.35)",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    marginTop: theme.spacing(1.5),
  },
}));

const RelatedRow = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(4),
  borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 14,
}));

const RelatedLabel = styled("span")({
  fontSize: "0.82rem",
  fontWeight: 500,
  color: "rgba(255, 255, 255, 0.5)",
  marginRight: 8,
});

const RelatedLink = styled("button")({
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#fff",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textDecoration: "underline",
  textDecorationColor: "rgba(255,255,255,0.3)",
  textUnderlineOffset: 4,
  fontFamily: "inherit",
  transition: "color 0.2s ease",
  "&:hover": { color: "#7ec1e2" },
});

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 800,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 760,
});

const Card = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  display: "flex",
  flexDirection: "column",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { fontSize: 22 },
});

const CardTitle = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  flex: 1,
});

const FactBox = styled(Box)({
  padding: 22,
  borderRadius: 10,
  backgroundColor: "#f8fafc",
  border: "1px solid rgba(15, 23, 42, 0.06)",
});

const FactValue = styled(Typography)({
  fontSize: "1.6rem",
  fontWeight: 700,
  color: "#0f172a",
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  marginBottom: 6,
});

const FactLabel = styled(Typography)({
  fontSize: "0.85rem",
  color: "rgba(15, 23, 42, 0.6)",
});

const TechCol = styled(Box)(({ theme }) => ({
  borderTop: "2px solid #2f81ad",
  paddingTop: theme.spacing(2.5),
  height: "100%",
}));

const TechHead = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#0f172a",
  marginBottom: 16,
});

const TechList = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const TechItem = styled("span")({
  fontSize: "0.82rem",
  fontWeight: 500,
  color: "#1d648c",
  padding: "5px 11px",
  borderRadius: 6,
  backgroundColor: "rgba(47, 129, 173, 0.06)",
  border: "1px solid rgba(47, 129, 173, 0.16)",
});

const StepIndex = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(15, 23, 42, 0.4)",
  marginBottom: 12,
});

const ProjectCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: "#fff",
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 12px 28px -16px rgba(15, 23, 42, 0.2)",
    "& img": { transform: "scale(1.04)" },
  },
});

const Thumb = styled("div")({
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 10",
  overflow: "hidden",
  backgroundColor: "#f1f5f9",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.4s ease",
  },
});

const IndustryChip = styled("span")({
  position: "absolute",
  top: 14,
  left: 14,
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#fff",
  padding: "4px 10px",
  borderRadius: 999,
  backgroundColor: "rgba(13, 22, 32, 0.85)",
  backdropFilter: "blur(4px)",
});

const QuoteBlock = styled(Box)({
  position: "relative",
  padding: "16px 16px 16px 20px",
  marginBottom: 16,
  borderLeft: "2px solid #2f81ad",
  backgroundColor: "rgba(47, 129, 173, 0.04)",
  borderRadius: "0 6px 6px 0",
});

const QuoteText = styled(Typography)({
  fontSize: "0.88rem",
  fontStyle: "italic",
  lineHeight: 1.55,
  color: "rgba(15, 23, 42, 0.78)",
});

const Meta = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "0.78rem",
  color: "rgba(15, 23, 42, 0.55)",
  marginTop: "auto",
});

const MetaRole = styled("span")({
  fontWeight: 600,
  color: "rgba(15, 23, 42, 0.75)",
});

const CtaBanner = styled(Box)(({ theme }) => ({
  backgroundColor: "#0d1620",
  color: "#fff",
  padding: theme.spacing(7, 4),
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(5, 3.5),
  },
}));

const CtaText = styled(Box)({ maxWidth: 600 });

const CtaTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.65rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#fff",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: { fontSize: "1.4rem" },
}));

const CtaLead = styled(Typography)({
  fontSize: "0.98rem",
  color: "rgba(255, 255, 255, 0.7)",
  lineHeight: 1.55,
});

const FaqItem = styled(Accordion)({
  marginBottom: 10,
  borderRadius: 10,
  border: "1px solid rgba(15, 23, 42, 0.1)",
  boxShadow: "none",
  backgroundColor: "#fff",
  overflow: "hidden",
  "&::before": { display: "none" },
  "&.Mui-expanded": {
    margin: "0 0 10px 0",
    borderColor: "rgba(47, 129, 173, 0.4)",
  },
});

const FaqSummary = styled(AccordionSummary)({
  padding: "4px 22px",
  minHeight: 64,
  "& .MuiAccordionSummary-content": { margin: "16px 0" },
  "& .MuiAccordionSummary-expandIconWrapper": { color: "#2f81ad" },
  "&.Mui-expanded": { minHeight: 64 },
});

const Question = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#0f172a",
  lineHeight: 1.45,
});

const Answer = styled(Typography)({
  fontSize: "0.95rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.7)",
});

const SERVICES = [
  {
    icon: <LanguageOutlinedIcon />,
    title: "Custom web software development",
    description:
      "Full-cycle web product development — discovery, UX/UI design, architecture, engineering, QA, and deployment by a single senior team.",
  },
  {
    icon: <DesignServicesOutlinedIcon />,
    title: "Front-end development",
    description:
      "Responsive interfaces engineered with React, Next.js, Vue, or Angular. Performance budgets, accessibility, and design-system discipline built in.",
  },
  {
    icon: <StorageOutlinedIcon />,
    title: "Back-end development",
    description:
      "Secure, scalable backends in Node.js, Python, or Go — with proper auth, observability, and CI/CD from day one.",
  },
  {
    icon: <SwapHorizOutlinedIcon />,
    title: "Legacy modernization",
    description:
      "Migrating off PHP monoliths, jQuery front-ends, or unsupported frameworks — without breaking existing workflows or SEO.",
  },
  {
    icon: <HubOutlinedIcon />,
    title: "API development & integration",
    description:
      "REST and GraphQL APIs, third-party integrations (Stripe, Twilio, SendGrid), and custom internal APIs with full documentation.",
  },
  {
    icon: <CloudOutlinedIcon />,
    title: "Cloud-native development",
    description:
      "Apps built for AWS, GCP, or Azure with container orchestration, autoscaling, and infrastructure as code.",
  },
  {
    icon: <SettingsSuggestOutlinedIcon />,
    title: "DevOps & automation",
    description:
      "CI/CD pipelines, monitoring, alerts, and zero-downtime deploys. Your team ships faster with fewer incidents.",
  },
  {
    icon: <StorageRoundedIcon />,
    title: "Database engineering",
    description:
      "Schema design, performance tuning, migrations, and replication strategies for PostgreSQL, MySQL, and MongoDB.",
  },
];

const BENEFITS = [
  {
    icon: <PhoneIphoneOutlinedIcon />,
    title: "Responsive by default",
    description:
      "Mobile-first design and engineering. Every interface is tested across breakpoints with real-device QA before launch.",
  },
  {
    icon: <TouchAppOutlinedIcon />,
    title: "Usability that drives conversion",
    description:
      "UX/UI grounded in research — flows that reduce friction, lower bounce rates, and improve activation funnels measurably.",
  },
  {
    icon: <ShieldOutlinedIcon />,
    title: "Security & reliability",
    description:
      "Threat modeling, OWASP-aligned code reviews, automated security testing, and uptime monitoring built into delivery.",
  },
  {
    icon: <TuneOutlinedIcon />,
    title: "Customization without bloat",
    description:
      "Personalization that fits your business rules and your budget — no enterprise SaaS overhead, no unused features.",
  },
  {
    icon: <EngineeringOutlinedIcon />,
    title: "Easy to maintain",
    description:
      "Code we hand over is documented, tested, and structured so your team can read it, change it, and deploy it.",
  },
  {
    icon: <RocketLaunchOutlinedIcon />,
    title: "Built to scale",
    description:
      "Architectures that survive your next 10× growth — observability, caching, and horizontal scaling from day one.",
  },
];

const PROCESS = [
  {
    n: "01",
    icon: <SearchOutlinedIcon />,
    title: "Discovery & planning",
    description:
      "We map your goals, users, and technical constraints. Output: a written scope, milestone timeline, and price estimate.",
  },
  {
    n: "02",
    icon: <ArchitectureOutlinedIcon />,
    title: "Design & prototyping",
    description:
      "UX flows, wireframes, and a clickable prototype. We validate the riskiest assumptions before production code.",
  },
  {
    n: "03",
    icon: <CodeOutlinedIcon />,
    title: "Development",
    description:
      "Two-week sprints, weekly demos, working software every Friday. Code in your repo from day one with CI included.",
  },
  {
    n: "04",
    icon: <BugReportOutlinedIcon />,
    title: "Testing & QA",
    description:
      "Unit, integration, accessibility, and performance testing. Stable, secure, and tested before anything ships.",
  },
  {
    n: "05",
    icon: <CloudDoneOutlinedIcon />,
    title: "Deployment",
    description:
      "Production rollout with monitoring, alerts, and observability. Cloud-native or on-prem — whichever fits your infrastructure.",
  },
  {
    n: "06",
    icon: <SupportAgentOutlinedIcon />,
    title: "Support & enhancement",
    description:
      "SLA-backed support, monitoring, and ongoing feature work. We maintain what we build long after launch.",
  },
];

const TECH_CATEGORIES = [
  {
    title: "Frontend",
    items: ["React", "Next.js", "TypeScript", "Vue.js", "Angular", "Tailwind", "Material UI", "D3.js"],
  },
  {
    title: "Backend",
    items: ["Node.js", "Express", "NestJS", "Python", "Django", "FastAPI", "Go", "GraphQL"],
  },
  {
    title: "Data storage",
    items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch", "DynamoDB"],
  },
  {
    title: "Cloud & DevOps",
    items: ["AWS", "GCP", "Azure", "Docker", "Kubernetes", "Terraform", "GitHub Actions"],
  },
  {
    title: "API integrations",
    items: ["Stripe", "Twilio", "SendGrid", "Auth0", "Algolia", "OpenAI", "Datadog"],
  },
];

const CASE_STUDIES = [
  {
    title: "Trading & portfolio management platform",
    industry: "Fintech",
    description:
      "Live market data, order execution, and position tracking. Full web client and broker-integration layer with sub-second update latency.",
    image: brandedPlaceholder("Trade Locker"),
    quote:
      "The reliability and quality of the work done by the team are impressive.",
    role: "Chief Architect",
    location: "USA",
  },
  {
    title: "Headless commerce platform",
    industry: "E-commerce",
    description:
      "Customizable storefront, inventory, payments, analytics, and marketing tools in a single headless stack.",
    image: brandedPlaceholder("EcomGleam"),
    quote:
      "Their senior engineers became part of our team — every sprint shipped real progress.",
    role: "VP Engineering",
    location: "USA",
  },
  {
    title: "Healthcare management system",
    industry: "Healthcare",
    description:
      "HIPAA-compliant clinic platform — patient records, appointments, billing, and insurance processing for small hospitals.",
    image: brandedPlaceholder("HealthTrack Pro"),
    quote:
      "We needed compliance expertise and engineering depth. They delivered both.",
    role: "Head of Product",
    location: "USA",
  },
];

const DIFFERENTIATORS = [
  {
    title: "Dedication to client success",
    description:
      "Your outcomes drive ours. Every sprint, every demo, every decision is aligned with what your business actually needs to achieve.",
  },
  {
    title: "Proven expertise",
    description:
      "Six years shipping production web apps across fintech, healthcare, SaaS, logistics, and e-commerce — with real case studies.",
  },
  {
    title: "Senior talent only",
    description:
      "No junior pyramids. The team that scopes your project ships it — same engineers from kickoff to launch and beyond.",
  },
  {
    title: "Strong delivery culture",
    description:
      "Two-week sprints, working software every Friday, transparent communication. We've shipped this way since 2019.",
  },
];

const FAQ = [
  {
    q: "What kind of web development services do you offer?",
    a: "Custom web software development, front-end and back-end development, legacy modernization, API development and integration, cloud-native development, DevOps, and database engineering. We take projects end-to-end or augment your existing team.",
  },
  {
    q: "What is your web development process?",
    a: "Six stages: Discovery, Design, Development, Testing, Deployment, and Support. Two-week sprints, weekly demos, working software every Friday. You always know what's happening this week, what's next, and when it ships.",
  },
  {
    q: "How do you ensure security and quality?",
    a: "Security-first development with threat modeling, OWASP-aligned reviews, and automated testing. Compliance support for GDPR, HIPAA, and SOC 2 when needed. 99% uptime targets backed by CI/CD pipelines and observability.",
  },
  {
    q: "Do you provide support and maintenance after launch?",
    a: "Yes — proactive monitoring, updates, bug fixes, performance tuning, and feature work under an SLA. We maintain what we build long after the initial launch.",
  },
  {
    q: "What sets you apart from other web development companies?",
    a: "Senior team from kickoff to launch (no junior pyramids), in-house AI practice, transparent fixed-price or T&M contracting, and 100% IP transfer on final payment — all in writing.",
  },
  {
    q: "What experience do you have?",
    a: "Founded in 2019. Over 25 products shipped across fintech, healthcare, SaaS, logistics, education, and e-commerce. Senior engineering team of 20 across Ontario and Lahore offices.",
  },
];

const WebDevelopmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [servicesVisible, setServicesVisible] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const node = servicesRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setServicesVisible(true),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      <PageTitle title="Web Development" />
      <Navbar />

      <HeroBg component="section" aria-labelledby="web-heading">
        <Container maxWidth="lg">
          <Box
            sx={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <HeroLabel>Web development</HeroLabel>
            <HeroTitle id="web-heading" variant="h1">
              Web application development services.
            </HeroTitle>
            <HeroLead>
              Responsive, scalable platforms engineered for sustainable growth.
              From custom web apps to legacy modernization — designed and built
              by senior engineers, end to end.
            </HeroLead>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <PrimaryButton
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/contacts")}
                disableElevation
              >
                Start your project
              </PrimaryButton>
              <GhostButton onClick={() => navigate("/portfolio")}>
                See case studies
              </GhostButton>
            </Box>
            <RelatedRow>
              <RelatedLabel>Related services:</RelatedLabel>
              <RelatedLink onClick={() => navigate("/mobileapp")}>
                Mobile development
              </RelatedLink>
              <RelatedLink onClick={() => navigate("/lms")}>
                Custom software
              </RelatedLink>
              <RelatedLink onClick={() => navigate("/Technologies")}>
                Tech stack
              </RelatedLink>
            </RelatedRow>
          </Box>
        </Container>
      </HeroBg>

      <Box component="section" sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4 }}>
            <Label>What we offer</Label>
            <SectionTitle variant="h2">
              A web development partner you can rely on
            </SectionTitle>
            <Lead>
              Smart IT Innovation is a senior software team building web
              applications tailored for performance, scalability, security,
              and integration with the systems you already run. From discovery
              to deployment — one team, fixed price or T&amp;M.
            </Lead>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <FactBox>
                <FactValue>Est. 2019</FactValue>
                <FactLabel>6+ years shipping</FactLabel>
              </FactBox>
            </Grid>
            <Grid item xs={6} md={3}>
              <FactBox>
                <FactValue>25+</FactValue>
                <FactLabel>Products delivered</FactLabel>
              </FactBox>
            </Grid>
            <Grid item xs={6} md={3}>
              <FactBox>
                <FactValue>20</FactValue>
                <FactLabel>Senior engineers</FactLabel>
              </FactBox>
            </Grid>
            <Grid item xs={6} md={3}>
              <FactBox>
                <FactValue>100%</FactValue>
                <FactLabel>IP transfer</FactLabel>
              </FactBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        ref={servicesRef}
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Our services</Label>
            <SectionTitle variant="h2">Web development services</SectionTitle>
            <Lead>
              Eight focused areas covering the full lifecycle of a modern web
              application — from greenfield builds to maintenance and scaling.
            </Lead>
          </Box>
          <Grid container spacing={2.5}>
            {SERVICES.map((s, i) => (
              <Grid item xs={12} sm={6} md={4} key={s.title}>
                <Box
                  sx={{
                    height: "100%",
                    opacity: servicesVisible ? 1 : 0,
                    transform: servicesVisible ? "translateY(0)" : "translateY(12px)",
                    transition: `opacity 0.45s ease ${
                      0.05 + i * 0.04
                    }s, transform 0.45s ease ${0.05 + i * 0.04}s`,
                  }}
                >
                  <Card>
                    <IconBadge>{s.icon}</IconBadge>
                    <CardTitle>{s.title}</CardTitle>
                    <CardCopy>{s.description}</CardCopy>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Technologies</Label>
            <SectionTitle variant="h2">The technologies we use</SectionTitle>
            <Lead>
              We pick the right tool for the job — no one-size-fits-all stacks.
              Below is a snapshot of what our team works with day to day.
            </Lead>
          </Box>
          <Grid container spacing={3}>
            {TECH_CATEGORIES.map((cat) => (
              <Grid item xs={12} sm={6} md={4} key={cat.title}>
                <TechCol>
                  <TechHead>{cat.title}</TechHead>
                  <TechList>
                    {cat.items.map((it) => (
                      <TechItem key={it}>{it}</TechItem>
                    ))}
                  </TechList>
                </TechCol>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Why web</Label>
            <SectionTitle variant="h2">
              How custom web development helps your business
            </SectionTitle>
            <Lead>
              Off-the-shelf tools rarely match how your team actually works.
              Custom web apps unlock measurable wins across UX, security,
              integration, and operational efficiency.
            </Lead>
          </Box>
          <Grid container spacing={2.5}>
            {BENEFITS.map((b) => (
              <Grid item xs={12} sm={6} md={4} key={b.title}>
                <Card>
                  <IconBadge>{b.icon}</IconBadge>
                  <CardTitle>{b.title}</CardTitle>
                  <CardCopy>{b.description}</CardCopy>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Process</Label>
            <SectionTitle variant="h2">Our web development process</SectionTitle>
            <Lead>
              Six stages, predictable delivery. You always know what's
              happening this week, what's next, and when it ships.
            </Lead>
          </Box>
          <Grid container spacing={2.5}>
            {PROCESS.map((step) => (
              <Grid item xs={12} sm={6} md={4} key={step.n}>
                <Card>
                  <IconBadge>{step.icon}</IconBadge>
                  <StepIndex>Step {step.n}</StepIndex>
                  <CardTitle>{step.title}</CardTitle>
                  <CardCopy>{step.description}</CardCopy>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Case studies</Label>
            <SectionTitle variant="h2">Web platforms we've shipped</SectionTitle>
            <Lead>
              A few selected products our senior engineers have designed,
              built, and delivered for clients across North America.
            </Lead>
          </Box>
          <Grid container spacing={2.5}>
            {CASE_STUDIES.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.title}>
                <ProjectCard>
                  <Thumb>
                    <img src={c.image} alt={c.title} loading="lazy" />
                    <IndustryChip>{c.industry}</IndustryChip>
                  </Thumb>
                  <Box
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardTitle>{c.title}</CardTitle>
                    <CardCopy sx={{ mb: 2 }}>{c.description}</CardCopy>
                    <QuoteBlock>
                      <FormatQuoteIcon
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 10,
                          color: "rgba(47, 129, 173, 0.25)",
                          fontSize: 22,
                        }}
                      />
                      <QuoteText>"{c.quote}"</QuoteText>
                    </QuoteBlock>
                    <Meta>
                      <MetaRole>{c.role}</MetaRole>
                      <span>{c.location}</span>
                    </Meta>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <CtaBanner>
            <CtaText>
              <CtaTitle>Looking for a web app that stands out?</CtaTitle>
              <CtaLead>
                Let's build a web product that combines functionality,
                creativity, and performance — set apart by a senior team that
                stays with you from kickoff to launch.
              </CtaLead>
            </CtaText>
            <PrimaryButton
              onClick={() => navigate("/contacts")}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              disableElevation
              sx={{ whiteSpace: "nowrap" }}
            >
              Contact us
            </PrimaryButton>
          </CtaBanner>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Why us</Label>
            <SectionTitle variant="h2">
              What makes us a great web development partner
            </SectionTitle>
          </Box>
          <Grid container spacing={2.5}>
            {DIFFERENTIATORS.map((d) => (
              <Grid item xs={12} sm={6} key={d.title}>
                <Card>
                  <CardTitle sx={{ fontSize: "1.18rem", mb: 1.5 }}>
                    {d.title}
                  </CardTitle>
                  <CardCopy>{d.description}</CardCopy>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}>
        <Container maxWidth="md">
          <Box sx={{ mb: { xs: 5, md: 6 } }}>
            <Label>FAQ</Label>
            <SectionTitle variant="h2">
              Web development — answers to common questions
            </SectionTitle>
          </Box>
          {FAQ.map((f, i) => (
            <FaqItem key={i} defaultExpanded={i === 0}>
              <FaqSummary expandIcon={<ExpandMoreIcon />}>
                <Question>{f.q}</Question>
              </FaqSummary>
              <AccordionDetails sx={{ p: "0 22px 22px" }}>
                <Answer>{f.a}</Answer>
              </AccordionDetails>
            </FaqItem>
          ))}
        </Container>
      </Box>

      <LetsTalk />
      <Footer />
    </Box>
  );
};

export default WebDevelopmentPage;
